import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { BellRing, Mail, MessageSquare, Smartphone, Lock, Sunrise } from "lucide-react";

const CHANNELS = [
  { key: "email", label: "Email", Icon: Mail, hint: "Where the detail goes — statements, contracts, case updates." },
  { key: "sms", label: "Text message", Icon: MessageSquare, hint: "Short, urgent things only. Needs a mobile on your account." },
  { key: "push", label: "Push notification", Icon: Smartphone, hint: "Straight to your phone or desktop if you've allowed it." },
];

const ORDER = ["progress", "offers", "funded", "documents", "callbacks", "commission", "chat", "reminders", "digest", "account"];

/**
 * NotifyPrefs — the partner decides what reaches them. Contracts awaiting signature and new
 * cases on their desk always arrive by email; those two are the whole point of the account.
 */
export const NotifyPrefs = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/portal/notify-prefs").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  const flip = async (key, value, label) => {
    const prev = data.prefs[key];
    setBusy(key);
    setData((d) => ({ ...d, prefs: { ...d.prefs, [key]: value } }));
    try {
      const r = await api.patch("/portal/notify-prefs", { [key]: value });
      setData(r.data);
      toast.success(label);
    } catch {
      setData((d) => ({ ...d, prefs: { ...d.prefs, [key]: prev } }));
      toast.error("Could not save that");
    } finally { setBusy(null); }
  };

  const restore = async (c, chans) => {
    try {
      const r = await api.patch("/portal/notify-prefs", Object.fromEntries(chans.map(({ key }) => [`mute_${key}_${c}`, false])));
      setData(r.data);
      toast.success("Back on");
    } catch { toast.error("Could not save that"); }
  };

  if (!data) return null;
  const { prefs, categories, essential } = data;
  const muteable = ORDER.filter((c) => categories[c] && !essential.includes(c));
  const always = data.kind === "lender" ? essential : essential.filter((e) => e !== "new_deal");

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="notify-prefs">
      <div className="flex items-center gap-2.5">
        <span className="grid place-items-center size-10 rounded-xl border border-slate-200 bg-slate-50 text-slate-600">
          <BellRing className="size-4.5" />
        </span>
        <div>
          <div className="text-[15px] font-semibold text-slate-900">What reaches you, and how</div>
          <div className="text-[12px] text-slate-500">Turn off anything you don't need. You can change it back any time.</div>
        </div>
      </div>

      {/* Channels */}
      <div className="mt-4 space-y-2">
        {CHANNELS.map(({ key, label, Icon, hint }) => {
          const off = !prefs[key];
          const noPhone = key === "sms" && !data.has_phone;
          return (
            <div key={key} className={`flex items-start justify-between gap-4 rounded-xl border p-3.5 transition-colors ${
              off ? "border-slate-200 bg-slate-50/60" : "border-slate-200 bg-white"}`}>
              <div className="flex items-start gap-2.5 min-w-0">
                <Icon className={`size-4 mt-0.5 shrink-0 ${off ? "text-slate-300" : "text-[#0055FF]"}`} />
                <div className="min-w-0">
                  <div className="text-[13.5px] font-semibold text-slate-900">{label}</div>
                  <div className="text-[11.5px] text-slate-500">
                    {noPhone ? "Add a mobile number to your details and we'll switch this on." : hint}
                  </div>
                </div>
              </div>
              <Switch checked={!!prefs[key]} disabled={busy === key || noPhone}
                      onCheckedChange={(v) => flip(key, v, v ? `${label} on` : `${label} off`)}
                      data-testid={`notify-channel-${key}`} />
            </div>
          );
        })}
      </div>

      {/* The morning brief — introducers only */}
      {data.brief_available && (
        <div className={`mt-4 rounded-xl border p-3.5 transition-colors ${
          prefs.daily_brief && !prefs.mute_digest ? "border-[#0055FF]/25 bg-[#0055FF]/[0.04]" : "border-slate-200 bg-slate-50/60"}`}
             data-testid="notify-brief">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-2.5 min-w-0">
              <Sunrise className={`size-4 mt-0.5 shrink-0 ${
                prefs.daily_brief && !prefs.mute_digest ? "text-[#0055FF]" : "text-slate-300"}`} />
              <div className="min-w-0">
                <div className="text-[13.5px] font-semibold text-slate-900">Your daily round-up</div>
                <div className="text-[11.5px] text-slate-500">
                  {data.kind === "lender" ? (
                    <>
                      One email at <b>{data.brief_hour || "5pm UK on weekdays"}</b> with everything sitting on
                      your desk — cases waiting on your decision, what you're reviewing, offers you have out,
                      notes and replies. New cases and your client's documents still reach you straight away.
                    </>
                  ) : (
                    <>
                      One email at <b>5pm UK on weekdays</b> with everything that moved on your cases that day
                      — updates from the desks, notes, paperwork. Offers, funded deals, anything a lender needs
                      from you and commission payments still reach you straight away by email and text.
                    </>
                  )}
                </div>
              </div>
            </div>
            <Switch checked={!!prefs.daily_brief} disabled={busy === "daily_brief" || !!prefs.mute_digest}
                    onCheckedChange={(v) => flip("daily_brief", v, v ? "Round-up on" : "Round-up off")}
                    data-testid="notify-brief-toggle" />
          </div>
          {prefs.mute_digest && (
            <div className="mt-2 text-[11px] text-slate-500" data-testid="notify-brief-muted">
              Summaries are muted below, so nothing will send until you switch those back on.
            </div>
          )}
          {!prefs.daily_brief && !prefs.mute_digest && (
            <div className="mt-2 text-[11px] text-slate-500" data-testid="notify-brief-off">
              {data.kind === "lender"
                ? "Off — cases will still land in your portal and by email as they arrive, there just won't be a daily list."
                : "Off — you'll still get each alert as it happens, there just won't be a daily round-up."}
            </div>
          )}
        </div>
      )}

      {/* Categories */}
      <div className="mt-5 pt-4 border-t border-slate-100">
        <div className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-600">Mute the things you don't need</div>
        <div className="mt-2.5 divide-y divide-slate-100">
          {muteable.map((c) => {
            const partial = prefs[`mute_${c}`] ? [] : CHANNELS.filter(({ key }) => prefs[`mute_${key}_${c}`]);
            return (
              <div key={c} className="py-2.5">
                <div className="flex items-center justify-between gap-4">
                  <div className="text-[13px] text-slate-800 min-w-0">{categories[c]}</div>
                  <Switch checked={!prefs[`mute_${c}`]} disabled={busy === `mute_${c}`}
                          onCheckedChange={(v) => flip(`mute_${c}`, !v, v ? "Back on" : "Muted")}
                          data-testid={`notify-cat-${c}`} />
                </div>
                {partial.length > 0 && (
                  <div className="mt-1 text-[11px] text-slate-500" data-testid={`notify-cat-${c}-partial`}>
                    {partial.map(({ label }) => label).join(" and ")} off for this only ·{" "}
                    <button type="button" className="font-semibold text-[#0044CC] hover:underline"
                            onClick={() => restore(c, partial)} data-testid={`notify-cat-${c}-restore`}>
                      turn back on
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Always-on */}
      <div className="mt-4 flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50/60 p-3">
        <Lock className="size-3.5 text-slate-400 mt-0.5 shrink-0" />
        <div className="text-[11.5px] text-slate-600" data-testid="notify-essential">
          {always.length === 1 ? "One thing always reaches" : "Two things always reach"} your inbox:{" "}
          <b>{always.map((e) => (categories[e] || e).toLowerCase()).join(", and ")}</b>.{" "}
          {always.length === 1 ? "A contract sitting unsigned costs you money" : "A contract sitting unsigned or a case you never saw costs you money"},
          so we won't let you miss {always.length === 1 ? "it" : "those"} — though texts and push alerts still follow your choices above.
        </div>
      </div>
    </div>
  );
};
